import { useTranslation } from "react-i18next";
import { EditIcon } from "@/components/icons/action-icons";
import { useState } from "react";
import * as yup from "yup";
import { useFormik, type FormikProps } from "formik";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { PlusSquare } from "lucide-react";
import { getErrorMessage } from "@/utils/error.utils";

import { SettingsInternalPageHeader } from "@/components/SettingsInternalPageHeader";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/v2/spinner";
import { cn } from "@/utils/cn";
import {
  useCreateTeamRoleMutation,
  useGetTeamRoleByIdQuery,
  useUpdateTeamRoleMutation,
} from "@/services/(company)/api.teams";
import { usePageTitle } from "@/hooks/usePageTitle";
import type { CreateTeamRolePayload } from "@/types/(company)/teams.interface";
import type { DYNAMIC_TEAM_ROLE_ID_KEY } from "@/routes/company/routes.teams";

// ── Validation ────────────────────────────────────────────────────────────────

const buildFormSchema = (t: (key: string) => string) =>
  yup.object({
    roleName: yup
      .string()
      .min(3, t("teams:roles.role_name_min"))
      .max(200, t("teams:roles.role_name_max"))
      .required(t("teams:roles.role_name_required")),
    roleDescription: yup
      .string()
      .max(500, t("teams:roles.description_max"))
      .nullable(),
    responsibilities: yup
      .array()
      .of(yup.string().trim().required(t("teams:roles.responsibility_empty")))
      .min(1, t("teams:roles.responsibility_min_one")),
    isActive: yup.boolean().default(true),
    isTeamManager: yup.boolean().default(false),
  });

export type Fields = yup.InferType<ReturnType<typeof buildFormSchema>>;

// ── Component ─────────────────────────────────────────────────────────────────

export const UpsertTeamRoles = () => {
  const { t } = useTranslation(["common", "company", "teams"]);
  const FormSchema = buildFormSchema(t);
  const { teamRoleId } = useParams<{ [DYNAMIC_TEAM_ROLE_ID_KEY]: string }>();
  usePageTitle(
    teamRoleId
      ? t("teams:roles.update_page_title")
      : t("teams:roles.add_page_title"),
  );

  const navigate = useNavigate();

  const { data: { data: roleDetails } = {}, isFetching } =
    useGetTeamRoleByIdQuery({ roleId: teamRoleId! }, { skip: !teamRoleId });

  const [createRole, { isLoading: isCreating }] = useCreateTeamRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateTeamRoleMutation();

  const isLoading = isCreating || isUpdating;

  const formik = useFormik<Fields>({
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: FormSchema,
    initialValues: {
      roleName: roleDetails?.roleName ?? "",
      roleDescription: roleDetails?.roleDescription ?? "",
      isTeamManager: roleDetails?.isTeamManager ?? false,
      isActive: roleDetails?.isActive ?? false,
      responsibilities: roleDetails?.responsibilities?.length
        ? roleDetails.responsibilities.map((role) => role.responsibility)
        : [],
    },
    onSubmit: async (formData) => {
      let payload: CreateTeamRolePayload;
      if (teamRoleId) {
        payload = {
          isActive: true,
          roleName: formData.roleName,
          roleDescription: formData.roleDescription ?? "",
          isTeamManager: formData.isTeamManager,
          responsibilities:
            formData.responsibilities?.map((res) => {
              if (typeof res === "object") return res;
              return { responsibility: res, isActive: true };
            }) ?? [],
        };
        await updateRole({ roleId: teamRoleId, payload })
          .unwrap()
          .then((res) => {
            toast.success(res?.message);
            navigate(-1);
          })
          .catch((e) =>
            toast.error(
              getErrorMessage(e, t("teams:roles.toast_failed_update")),
            ),
          );
      } else {
        payload = {
          isActive: true,
          roleName: formData.roleName,
          isTeamManager: formData.isTeamManager,
          responsibilities: formData.responsibilities?.length
            ? formData.responsibilities
            : [],
          roleDescription: formData.roleDescription ?? "",
        };
        await createRole(payload)
          .unwrap()
          .then((resp) => {
            toast.success(resp?.message);
            navigate(-1);
          })
          .catch((e) =>
            toast.error(getErrorMessage(e, t("teams:roles.toast_failed_add"))),
          );
      }
    },
  });

  return (
    <section className="flex flex-col gap-4 h-full pt-2">
      <SettingsInternalPageHeader
        title={
          teamRoleId
            ? t("teams:roles.header_update_title")
            : t("teams:roles.header_add_title")
        }
        description={
          teamRoleId
            ? t("teams:roles.header_update_description")
            : t("teams:roles.header_add_description")
        }
        isUpsert
        onBack={() => navigate(-1)}
      />

      {isFetching ? (
        <Spinner />
      ) : (
        <form
          className="flex flex-col flex-1 min-h-0 bg-white rounded-xs border"
          onSubmit={formik.handleSubmit}
        >
          <div className="flex-1 overflow-y-auto scrollbar-thin p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field className="col-span-1">
                <FieldLabel htmlFor={"roleName" satisfies keyof Fields}>
                  {t("teams:roles.role_name_label")}
                </FieldLabel>
                <Input
                  id={"roleName" satisfies keyof Fields}
                  placeholder={t("common:placeholders.name_of_the_role")}
                  onChange={formik.handleChange}
                  value={formik.values.roleName}
                  className={cn(formik.errors.roleName && "border-danger")}
                />
                <FieldError>
                  {formik.touched.roleName && formik.errors.roleName
                    ? formik.errors.roleName
                    : null}
                </FieldError>
              </Field>

              <div className="md:col-span-2">
                <AddResposField
                  formik={formik}
                  fieldName={"responsibilities" satisfies keyof Fields}
                />
              </div>

              <Field orientation="horizontal" className="md:col-span-2">
                <Checkbox
                  className="w-5 h-5"
                  checked={formik.values.isTeamManager}
                  onCheckedChange={(checked) =>
                    formik.setFieldValue(
                      "isTeamManager" satisfies keyof Fields,
                      checked,
                    )
                  }
                  id={"isTeamManager" satisfies keyof Fields}
                  name={"isTeamManager" satisfies keyof Fields}
                />
                <Label
                  className="text-black"
                  htmlFor={"isTeamManager" satisfies keyof Fields}
                >
                  {t("teams:roles.is_team_manager_label")}
                </Label>
              </Field>
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-36"
            >
              {t("teams:roles.cancel")}
            </Button>
            <PermissionButton
              type="submit"
              section={PLATFORM_SECTION_ENUM.TEAMS}
              action="settings"
              mode="hide"
              variant="primary"
              disabled={isLoading}
              className="w-36"
            >
              {isLoading
                ? teamRoleId
                  ? t("teams:roles.updating")
                  : t("teams:roles.saving")
                : teamRoleId
                  ? t("teams:roles.update")
                  : t("teams:roles.save")}
            </PermissionButton>
          </div>
        </form>
      )}
    </section>
  );
};

// ── Responsibilities field ─────────────────────────────────────────────────────

const AddResposField = ({
  formik,
  fieldName,
}: {
  formik: FormikProps<Fields>;
  fieldName: keyof Fields;
}) => {
  const { t } = useTranslation(["common", "teams"]);
  const [newResp, setNewResp] = useState("");
  const [isAddingNewResp, setIsAddingNewResp] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleAdd = () => {
    if (newResp.trim()) {
      formik.setFieldValue("responsibilities" satisfies keyof Fields, [
        ...(formik.values.responsibilities ?? []),
        newResp,
      ]);
      setNewResp("");
      setIsAddingNewResp(false);
    }
  };

  const handleSaveEdit = (index: number) => {
    const updated = [...(formik.values.responsibilities ?? [])];
    updated[index] = editValue;
    formik.setFieldValue(fieldName, updated);
    setEditingIndex(null);
    setEditValue("");
  };

  return (
    <Field
      className={cn(
        "border rounded-xs p-4",
        formik.errors.responsibilities &&
          formik.touched.responsibilities &&
          "border-danger",
      )}
    >
      <FieldLabel>{t("teams:roles.responsibilities_label")}</FieldLabel>

      {formik.values.responsibilities?.map((item, i) => (
        <div
          key={i}
          className="flex justify-between items-center text-sm border rounded-xs p-3"
        >
          <div className="flex items-center gap-2 flex-1">
            <span className="size-6 rounded-full bg-primary text-white text-[10px] grid place-items-center shrink-0">
              R{i + 1}
            </span>
            {editingIndex === i ? (
              <Input
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveEdit(i);
                  }
                }}
              />
            ) : (
              <p>{item}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {editingIndex === i ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSaveEdit(i)}
                  className="text-xs text-primary"
                >
                  {t("teams:roles.resp_save")}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingIndex(null)}
                  className="text-xs text-muted-foreground"
                >
                  {t("teams:roles.resp_cancel")}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setEditingIndex(i);
                    setEditValue(item);
                  }}
                  className="text-primary"
                >
                  <EditIcon size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (formik.values.responsibilities)
                      formik.setFieldValue(
                        fieldName,
                        formik.values.responsibilities.filter(
                          (_, idx) => idx !== i,
                        ),
                      );
                  }}
                  className="text-danger hover:underline text-xs"
                >
                  {t("teams:roles.resp_remove")}
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      {isAddingNewResp ? (
        <div className="flex items-center gap-3">
          <Input
            autoFocus
            value={newResp}
            placeholder={t("common:placeholders.enter_responsibility")}
            onChange={(e) => setNewResp(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleAdd())
            }
            className="flex-1"
          />
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={handleAdd}>
              {t("teams:roles.resp_save")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsAddingNewResp(false)}
            >
              {t("teams:roles.resp_cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsAddingNewResp(true)}
          className="max-w-max flex gap-1 text-primary text-sm font-semibold mt-2"
        >
          <PlusSquare strokeWidth={1.5} /> {t("teams:roles.resp_add_new")}
        </button>
      )}

      <FieldError>{formik.errors.responsibilities}</FieldError>
    </Field>
  );
};
